import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SettingsContext = createContext(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

const CURRENCY_SYMBOLS = {
  AED: 'د.إ',
  USD: '$',
  EUR: '€',
  GBP: '£',
  SAR: 'ر.س',
  INR: '₹',
  QAR: '﷼',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: '﷼',
  EGP: '£',
  PKR: '₨',
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    currency: 'AED',
    language: 'en',
    theme: 'dark',
    login_background: null,
  });
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_BASE_URL}/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSettings(response.data);
        applyTheme(response.data.theme);
      } else {
        // Load from localStorage if no token
        const savedSettings = localStorage.getItem('app_settings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setSettings(parsed);
          applyTheme(parsed.theme);
        }
      }
    } catch (error) {
      console.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanySettings = useCallback(async (companyId) => {
    if (!companyId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_BASE_URL}/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanySettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  }, []);

  const applyTheme = (theme) => {
    if (theme === 'light') {
      document.documentElement.classList.add('light-theme');
    } else {
      document.documentElement.classList.remove('light-theme');
    }
  };

  // Get effective currency - prioritize company settings
  const getEffectiveCurrency = useCallback(() => {
    return companySettings?.currency || settings.currency || 'AED';
  }, [companySettings, settings.currency]);

  const formatCurrency = useCallback((amount, overrideCurrency = null) => {
    const numAmount = Number(amount) || 0;
    const currency = overrideCurrency || companySettings?.currency || settings.currency || 'AED';
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Math.abs(numAmount));
    // \u200E (LRM) keeps the minus visually BEFORE RTL currency symbols (e.g. AED د.إ),
    // otherwise the bidi algorithm renders "-36.81" as "36.81-" and it reads as positive.
    return `${numAmount < 0 ? '\u200E\u2212' : ''}${symbol} ${formatted}`;
  }, [companySettings, settings.currency]);

  const updateSettings = async (newSettings) => {
    setSettings(newSettings);
    applyTheme(newSettings.theme);
    localStorage.setItem('app_settings', JSON.stringify(newSettings));
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        companySettings,
        loading,
        formatCurrency,
        updateSettings,
        loadSettings,
        loadCompanySettings,
        getEffectiveCurrency,
        currency: companySettings?.currency || settings.currency || 'AED',
        country: companySettings?.country || 'UAE',
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};